(() => {
  'use strict';

  const script = document.currentScript;
  const section = script?.closest('.thunderhead-signup');
  const stylesheetUrl = script?.dataset.thunderheadGridStylesheet;
  // Older engines retain Shopify's existing, functional single-column form.
  if (!section || !stylesheetUrl ||
      !CSS.supports('selector(:has(*))') ||
      !CSS.supports('container-type', 'inline-size')) return;

  const initialized = new WeakSet();
  const selectors = [
    '#first_name',
    '#email',
    '#phone_number',
    '[id="custom#pickup_area-selector-label"]',
    'fieldset[name="emailConsent"]',
    'fieldset[name="phoneConsent"]',
    'button[type="submit"]',
  ];

  const initialize = (host) => {
    const root = host.shadowRoot;
    if (!root || initialized.has(host)) return;
    initialized.add(host);

    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = stylesheetUrl;
    stylesheet.dataset.thunderheadGrid = '';
    let frame = 0;
    let shop = null;
    let activeLayout = null;
    let loaded = false;
    let failed = false;
    const shopResize = new ResizeObserver(() => schedule());

    const expectedStructure = () => {
      const form = root.querySelector('form');
      if (!form || root.querySelectorAll('form').length !== 1) return false;
      const allowedChildren = new Set();
      const fields = [];

      for (const selector of selectors) {
        const matches = form.querySelectorAll(selector);
        if (matches.length !== 1) return false;
        let child = matches[0];
        while (child.parentElement && child.parentElement !== form) child = child.parentElement;
        if (child.parentElement !== form) return false;
        allowedChildren.add(child);
        fields.push(child);
      }
      if (allowedChildren.size !== selectors.length) return false;

      const currentShop = form.querySelector('shop-lead-capture');
      if (currentShop !== shop) {
        shopResize.disconnect();
        shop = currentShop;
        if (shop) shopResize.observe(shop);
      }
      let shopContainer = null;
      if (shop) {
        // This layout is for the existing form with Continue with Shop off.
        // If that control is later enabled, leave its native layout intact.
        const bounds = shop.getBoundingClientRect();
        if (bounds.width > 0 && bounds.height > 0) return false;
        let child = shop;
        while (child.parentElement && child.parentElement !== form) child = child.parentElement;
        if (child.parentElement !== form) return false;
        allowedChildren.add(child);
        shopContainer = child;
      }
      const errors = [];
      const errorAnchors = new Map([
        [fields[1], 0], [fields[2], 1], [fields[3], 2], [fields[6], 3],
        [fields[4], 1], [fields[5], 2],
      ]);
      for (const child of form.children) {
        if (child.tagName !== 'OUTPUT' || child.getAttribute('role') !== 'alert') continue;
        let index = child.dataset.thunderheadGridError;
        if (index === undefined) {
          const associated = errorAnchors.get(child.nextElementSibling);
          if (associated === undefined) return false;
          index = String(associated);
          child.dataset.thunderheadGridError = index;
        }
        if (!/^[0-3]$/.test(index) || errors.some((error) => error.index === Number(index))) return false;
        errors.push({ index: Number(index), node: child });
        allowedChildren.add(child);
      }
      if (![...form.children].every((child) => allowedChildren.has(child))) return false;
      return { form, fields, shopContainer, errors };
    };

    const restoreNativeOrder = () => {
      if (activeLayout?.form.isConnected) {
        const { form, fields } = activeLayout;
        for (const [anchor, consent] of [[fields[1], fields[4]], [fields[2], fields[5]]]) {
          if (anchor.parentElement === form && consent.parentElement === form &&
              anchor.nextElementSibling !== consent) {
            form.insertBefore(consent, anchor.nextElementSibling);
          }
        }
        form.querySelectorAll('output[data-thunderhead-grid-error]').forEach((error) => {
          delete error.dataset.thunderheadGridError;
        });
      }
      activeLayout = null;
    };

    const matchVisualOrder = ({ form, fields, shopContainer, errors }) => {
      const desired = shopContainer ? [shopContainer] : [];
      fields.forEach((field, index) => {
        desired.push(field);
        const error = errors.find((item) => item.index === index);
        if (error) desired.push(error.node);
      });
      let anchor = null;
      for (let index = desired.length - 1; index >= 0; index--) {
        const child = desired[index];
        if (child.nextElementSibling !== anchor) form.insertBefore(child, anchor);
        anchor = child;
      }
    };

    const update = () => {
      frame = 0;
      if (!host.isConnected) {
        observer.disconnect();
        shopResize.disconnect();
        return;
      }
      if (failed) return;
      const layout = expectedStructure();
      if (layout) {
        if (activeLayout && activeLayout.form !== layout.form) restoreNativeOrder();
        activeLayout = layout;
        if (!stylesheet.isConnected) root.append(stylesheet);
        if (loaded) matchVisualOrder(layout);
      } else {
        stylesheet.remove();
        loaded = false;
        restoreNativeOrder();
      }
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    stylesheet.addEventListener('load', () => { loaded = true; schedule(); });
    stylesheet.addEventListener('error', () => {
      failed = true;
      stylesheet.remove();
      restoreNativeOrder();
    });
    // Move the existing consent wrappers within the same form so DOM, focus,
    // and visual order agree. No controls are cloned; values, native events,
    // validation, and submission requests remain owned by Shopify.
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    update();
  };

  let attempts = 0;
  const scan = () => {
    if (!section.isConnected) return;
    section.querySelectorAll('shopify-forms-embed').forEach(initialize);
    // Forms mounts asynchronously; keep this bounded and local to this section.
    if (++attempts < 120) setTimeout(scan, 250);
  };
  scan();
})();
